/**
 * FAB Component - Controller
 * ==========================
 */

import type { IScope, ISCEService, ITranscludeFunction } from 'angular';
import { hasTranscludedContent } from '../../_base/transclusion';
import {
  INTENT_EMPHASIS,
  INTENT_RING,
  SIZES,
  BASE_CLASSES,
  ICONS,
  SPINNER_SVG,
  DEFAULTS,
  FabIntent,
  FabEmphasis,
  FabSize,
  FabType,
  FabIconType
} from './constants';

export interface FabBindings {
  icon?: FabIconType;
  intent?: FabIntent;
  emphasis?: FabEmphasis;
  size?: FabSize;
  label?: string;
  ariaLabel?: string;
  disabled?: boolean;
  loading?: boolean;
  type?: FabType;
  onClick?: (args: { $event: Event }) => void;
}

interface BindingChanges {
  icon?: { currentValue: FabIconType };
  [key: string]: unknown;
}

export class FabController implements FabBindings {
  static $inject = ['$scope', '$sce', '$transclude'];

  private $scope: IScope | null;
  private $sce: ISCEService | null;
  private $transclude: ITranscludeFunction | null;

  icon?: FabIconType;
  intent?: FabIntent;
  emphasis?: FabEmphasis;
  size?: FabSize;
  label?: string;
  ariaLabel?: string;
  disabled?: boolean;
  loading?: boolean;
  type?: FabType;
  onClick?: (args: { $event: Event }) => void;

  trustedIcon: unknown = null;
  trustedSpinner: unknown = null;
  labelId = '';

  // Estado interno (modo compound: contenido transcluido reemplaza el ícono)
  hasContent: boolean = false;

  constructor($scope: IScope, $sce: ISCEService, $transclude: ITranscludeFunction) {
    this.$scope = $scope;
    this.$sce = $sce;
    this.$transclude = $transclude;
    this.handleClick = this.handleClick.bind(this);
  }

  $onInit(): void {
    this.intent = this.intent || DEFAULTS.intent;
    this.emphasis = this.emphasis || DEFAULTS.emphasis;
    this.size = this.size || DEFAULTS.size;
    this.type = this.type || DEFAULTS.type;
    this.labelId = 'ec-fab-label-' + (this.$scope ? this.$scope.$id : Math.random().toString(36).slice(2));
    this._updateTrusted();
    this.hasContent = hasTranscludedContent(this.$transclude);
    if (!this.label && !this.ariaLabel) {
      console.warn('[Fab] icon-only sin nombre accesible: define "label" o "aria-label".');
    }
  }

  $onChanges(changes: BindingChanges): void {
    if (changes.icon) {
      this._updateTrusted();
    }
  }

  $onDestroy(): void {
    this.$scope = null;
    this.$sce = null;
    this.$transclude = null;
  }

  private _updateTrusted(): void {
    if (!this.$sce) return;
    this.trustedIcon = this.$sce.trustAsHtml(ICONS[this.icon as FabIconType] || '');
    this.trustedSpinner = this.$sce.trustAsHtml(SPINNER_SVG);
  }

  getButtonClasses(): string[] {
    const intent = this.intent || DEFAULTS.intent;
    const emphasis = this.emphasis || DEFAULTS.emphasis;
    const size = SIZES[this.size || DEFAULTS.size];

    const classes = [BASE_CLASSES.button, size.button];

    if (this.isDisabled) {
      classes.push(BASE_CLASSES.disabled);
    } else {
      classes.push(INTENT_EMPHASIS[intent][emphasis], INTENT_RING[intent]);
    }

    return classes;
  }

  getIconClasses(): string[] {
    return [SIZES[this.size || DEFAULTS.size].icon];
  }

  getLabelClasses(): string[] {
    return [SIZES[this.size || DEFAULTS.size].label, BASE_CLASSES.label];
  }

  handleClick($event: Event): void {
    if (this.isDisabled) {
      $event.preventDefault();
      $event.stopPropagation();
      return;
    }
    if (typeof this.onClick === 'function') {
      this.onClick({ $event });
    }
  }

  get isDisabled(): boolean {
    return this.disabled === true || this.loading === true;
  }
}

export default FabController;
